// One thread item, one renderer per `type` (spec §13.15). Types the app
// doesn't know render nothing, so the backend can ship new ones first. The
// item's `state` shows on each: an undone or replaced message says so, a
// change or proposal shows its outcome, an answered question its answer.
//
//   coach_message   a bubble; body.kind 'chat' | 'request' | 'reaction' | 'morning_card' (else plain);
//                   body.saved (what the coach kept from the turn) adds "Saved to your calendar: …"
//                   body.charts (v2/coachChart.js) draws its charts under the words
//                   body.actions ['rebuild'] (a plan that couldn't be built) adds Try again;
//                   body.action_label names it otherwise (an empty calendar: Build my plan);
//                   body.actions ['fill', …] (an empty calendar the plan's outline covers) is one
//                   button instead, body.fill_label (Fill my next two weeks): POST /v2/plan/fill
//                   body.actions ['ai_consent'] (no AI coaching consent, aithlo-backend#64) adds Review consent
//                   body.followups (at most 3) are chips under it while it is the newest (v2/suggestions.js)
//                   body.cards, body.sources and body.steps show under its words (ReplyExtras)
//   athlete_message the athlete's bubble (with what it asked about, for an Ask why)
//   working         the coach's working line (a plan run: its steps and findings)
//   change          the change card (ChangeCard)
//   proposal        a strategy proposal (StrategyProposalCard) or a session-level one (ChangeCard)
//   plan_update     a compact line: what the coach wrote (a week, a block); a phase
//                   review (body.kind 'phase_review', M6) is a card with Open timeline
//   coach_message 'on_track' (M6) is a plain bubble; the race screen shows the latest
//   observation     a compact note from the coach, with Why?
//   system_action / athlete_action  compact system lines
//   safety          the safety card (SafetyCard)
//   question        an intake question with its answers as chips (QuestionCard)
import React, { lazy, Suspense } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CalendarCheck, Eye, HelpCircle, Loader2, RotateCcw, ShieldCheck, Undo2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ds';
import { cleanCoachText } from '../chat/sse';
import { RENDERED_ITEM_TYPES } from '../../v2/contract';
import { isStrategyProposal, localDay } from '../../v2/threadView';
import QuestionCard from './QuestionCard';
import StrategyProposalCard from './StrategyProposalCard';
import CreationWork from './CreationWork';
import ChangeCard from './ChangeCard';
import SafetyCard from './SafetyCard';
import MorningCard from './MorningCard';
import PhaseReviewCard from './PhaseReviewCard';
import { isPhaseReview } from '../../v2/season';
import { commands } from '../../v2/commands';
import { savedLines } from '../../v2/coachNotes';
import { drawableCharts } from '../../v2/coachChart';
import { REBUILDING_TEXT, itemOutcome, offersRebuild, rebuildAsked, rebuildCommand, rebuildLabel } from '../../v2/failures';
import { offersAiConsent, requestAiConsent } from '../../lib/aiConsent';
import { followupChips } from '../../v2/suggestions';
import { hasExtras } from '../../v2/replyExtras';
import SuggestionChips from './SuggestionChips';
import ReplyExtras from './ReplyExtras';

const MD = {
  p: ({ children }) => <p className="m-0 mb-1.5 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="my-1 ml-4 list-disc">{children}</ul>,
  ol: ({ children }) => <ol className="my-1 ml-4 list-decimal">{children}</ol>,
  // Tables (GFM): the coach writes them for race predictions and splits.
  table: ({ children }) => (
    <div className="my-1.5 max-w-full overflow-x-auto">
      <table className="border-collapse text-left">{children}</table>
    </div>
  ),
  th: ({ children, style }) => (
    <th style={style} className="border-b border-border-subtle px-2 py-1 font-semibold">{children}</th>
  ),
  td: ({ children, style }) => (
    <td style={style} className="border-b border-border-subtle px-2 py-1 tabular-nums">{children}</td>
  ),
};

const CoachChart = lazy(() => import('./CoachChart'));

/** The charts under a reply; one that doesn't match the contract isn't drawn. */
function Charts({ charts }) {
  const drawable = drawableCharts(charts);
  if (!drawable.length) return null;
  return (
    <Suspense fallback={<div className="mt-2 h-48" aria-hidden />}>
      {drawable.map((c, i) => <CoachChart key={`${c.title}-${i}`} chart={c} />)}
    </Suspense>
  );
}

/** What the coach kept from the turn (b.saved): "Saved to your calendar: …", "Noted for later: …". */
function SavedLines({ saved }) {
  return (
    <div className="mt-1 space-y-0.5">
      {savedLines(saved).map((line) => (
        <p key={line} className="m-0 flex items-start gap-1 type-caption text-text-tertiary">
          <CalendarCheck className="mt-px h-3 w-3 shrink-0" aria-hidden />{line}
        </p>
      ))}
    </div>
  );
}

const MESSAGE_STATE = { undone: 'Undone', superseded: 'Replaced', dismissed: 'Dismissed', accepted: 'Done' };

export function CoachText({ text, streaming = false, label = null, state = 'active' }) {
  const { text: clean } = cleanCoachText(text || '', { streaming });
  const note = MESSAGE_STATE[state];
  return (
    <div className="flex flex-col items-start">
      <div className={cn('w-full text-[14px] leading-[1.4] text-text-body', note && 'opacity-70')}>
        {label && <p className="m-0 mb-1 type-eyebrow uppercase text-text-tertiary">{label}</p>}
        <Markdown remarkPlugins={[remarkGfm]} components={MD}>{clean}</Markdown>
        {streaming && <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-border-strong align-middle" />}
      </div>
      {note && <span className="mt-1 flex items-center gap-1 type-caption text-text-tertiary"><Undo2 size={11} /> {note}</span>}
    </div>
  );
}

/** `failed` (a refused message, Figma 51:4384): { onRetry (null when a retry can't help), onRemove }. */
export function AthleteText({ text, pending, about = null, failed = null }) {
  return (
    <div className="flex flex-col items-end">
      {about && <span className="mb-1 flex items-center gap-1 type-caption text-text-tertiary"><HelpCircle size={11} /> About {about}</span>}
      <div className={cn('max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-bg-raised px-3 py-[9px] text-[14px] leading-[1.4] text-text-primary', pending && 'opacity-70')}>
        {text}
      </div>
      {pending && <span className="mt-1 type-caption text-text-tertiary">{pending === 'queued' ? 'Waiting for connection…' : 'Sending…'}</span>}
      {failed && (
        <span className="mt-1 flex items-center gap-1 type-caption text-status-danger" role="status">
          Not sent
          {failed.onRetry && (<> · <button type="button" onClick={failed.onRetry} className="underline-offset-2 hover:underline">Try again</button></>)}
          {' · '}<button type="button" onClick={failed.onRemove} className="underline-offset-2 hover:underline">Remove</button>
        </span>
      )}
    </div>
  );
}

/** A compact centred line: system and athlete actions. */
export function SystemLine({ text }) {
  return text ? <p className="m-0 text-center type-caption text-text-tertiary">{text}</p> : null;
}

function PlanUpdate({ item, onWhy }) {
  const b = item.body || {};
  return (
    <div className="flex items-start gap-2.5 rounded-chip border border-border-subtle bg-bg-surface px-3 py-2.5 type-body-small text-text-body" role="article"
      aria-label={`Plan update: ${b.text || b.title || ''}`}>
      <CalendarCheck size={14} className="mt-0.5 shrink-0 text-accent-default" />
      <span className="flex-1">{b.title && <span className="font-semibold text-text-primary">{b.title} · </span>}{b.text}</span>
      {onWhy && (
        <button type="button" onClick={() => onWhy(item)} className="shrink-0 type-label text-accent-default hover:text-accent-hover">Why?</button>
      )}
    </div>
  );
}

function Observation({ item, onWhy }) {
  const b = item.body || {};
  return (
    <div className="flex items-start gap-2.5 rounded-chip border border-border-subtle bg-bg-surface px-3 py-2.5 type-body-small text-text-body" role="note"
      aria-label={`Coach noticed: ${b.title || b.text || ''}`}>
      <Eye size={14} className="mt-0.5 shrink-0 text-text-tertiary" />
      <span className="flex-1">{b.title && <span className="font-semibold text-text-primary">{b.title}. </span>}{b.text}</span>
      {onWhy && (
        <button type="button" onClick={() => onWhy(item)} className="shrink-0 type-label text-accent-default hover:text-accent-hover">Why?</button>
      )}
    </div>
  );
}

/** The finished working item of the run that wrote this proposal, collapsed above it. */
export const workFor = (item, work) => {
  const run = item.refs?.agent_run_id && work?.[item.refs.agent_run_id];
  if (!run || (!run.steps.length && !run.insights.length)) return null;
  return { done: true, steps: run.steps, insights: run.insights, phases: [], sessions: 0 };
};

function Working({ item, work }) {
  const run = workFor(item, work);
  const active = item.state === 'active';
  if (run) return <CreationWork view={{ ...run, done: !active }} />;
  const b = item.body || {};
  const text = b.text || b.label || (active ? 'Working…' : null);
  if (!text) return null;
  return (
    <p className="m-0 flex items-center gap-2 type-caption text-text-tertiary" role="status">
      {active ? <Loader2 size={12} className="animate-spin text-accent-default" /> : <span className="h-1.5 w-1.5 rounded-full bg-border-strong" />}
      {text}
    </p>
  );
}

const REACTION_LABEL = 'After your session';

/** Try again under a coach message whose plan couldn't be built (or the message's own words, an empty
 * calendar's "Build my plan"): POST /v2/plan/rebuild, once. An empty calendar whose plan covers it
 * offers "Fill my next two weeks" instead: POST /v2/plan/fill (written from the plan, with Undo). */
export function RebuildButton({ item, onAct, pending = [], outcomes = {} }) {
  const asked = rebuildAsked(item, pending, outcomes);
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <Button size="small" disabled={asked} icon={<RotateCcw size={13} />} onClick={() => onAct?.(rebuildCommand(item, commands))}>
        {rebuildLabel(item)}
      </Button>
      {asked && (
        <span className="flex items-center gap-1 type-caption text-text-tertiary" role="status">
          <Loader2 size={11} className="animate-spin text-accent-default" /> {REBUILDING_TEXT}
        </span>
      )}
    </div>
  );
}

/** Review consent under the coach's "accept the AI coaching consent first": opens the prompt (Layout). */
export function AiConsentButton({ onReview = requestAiConsent }) {
  return (
    <div className="mt-2 flex items-center gap-2">
      <Button size="small" icon={<ShieldCheck size={13} aria-hidden />} onClick={() => onReview()}>Review consent</Button>
    </div>
  );
}

const NONE_REMOVED = new Set();

/**
 * One item. `onAct` sends a command; `onWhy(item)` opens the composer about
 * it; `onTalk(item)` the same from a safety card; `about(ref)` names what an
 * athlete message asked about. `now` decides a change card's 24 h.
 * `onOpenTimeline` opens the race screen (a phase review's button).
 * `readOnly` (a coach viewing the athlete): nothing that sends or asks shows.
 * `outcomes` (the client's): what an item's command answered in words (an
 * accept the coach is updating or kept, a rebuild asked for).
 * `onAsk(chip)` sends a chip; `latestCoach` is the coach message that may offer them.
 * `onOpen({ kind, id })` opens what a reply names; `thread` is the store's items (a confirm card's proposal).
 * `removedActivities` (a Set of Strava ids): activities a reply names that are gone; they show as removed, not to open.
 */
export default function ThreadItem({ item, onAsk = null, latestCoach = null, onOpen = null, thread = {}, readOnly = false, removedActivities = NONE_REMOVED, ...rest }) {
  const message = item.type === 'coach_message';
  const shown = message && !readOnly && onAsk && String(item.id) === latestCoach ? followupChips(item) : [];
  const extras = message && hasExtras(item.body);
  const own = itemBody({ item, readOnly, ...rest });
  if (!shown.length && !extras) return own;
  return (
    <div>
      {own}
      {extras && <ReplyExtras body={item.body} onOpen={onOpen} onAct={readOnly ? null : rest.onAct} thread={thread} pending={rest.pending}
        removedActivities={removedActivities} />}
      <SuggestionChips chips={shown} onPick={onAsk} className="mt-2" />
    </div>
  );
}

function itemBody({ item, onAct, onWhy, onTalk, busy = false, pending = [], work = {}, races = [],
  onAddRace, onOpenTimeline, about, now = Date.now(), today = localDay(), readOnly = false, outcomes = {} }) {
  if (!RENDERED_ITEM_TYPES.includes(item.type)) return null;
  if (readOnly) { onWhy = null; onTalk = null; onAct = null; onAddRace = null; }
  const b = item.body || {};
  switch (item.type) {
    case 'coach_message':
      if (b.kind === 'morning_card') return <MorningCard item={item} />;
      if (offersAiConsent(item) && !readOnly) {
        return (
          <div>
            <CoachText text={b.text} state={item.state} />
            <Charts charts={b.charts} />
            <AiConsentButton />
          </div>
        );
      }
      if (offersRebuild(item) && onAct && item.state === 'active') {
        return (
          <div>
            <CoachText text={b.text} state={item.state} />
            <Charts charts={b.charts} />
            <RebuildButton item={item} onAct={onAct} pending={pending} outcomes={outcomes} />
          </div>
        );
      }
      if (savedLines(b.saved).length || drawableCharts(b.charts).length) {
        return (
          <div>
            <CoachText text={b.text} state={item.state} label={b.kind === 'reaction' ? REACTION_LABEL : null} />
            <Charts charts={b.charts} />
            <SavedLines saved={b.saved} />
          </div>
        );
      }
      return <CoachText text={b.text} state={item.state} label={b.kind === 'reaction' ? REACTION_LABEL : null} />;
    case 'athlete_message':
      return <AthleteText text={b.text} about={b.context_ref ? about?.(b.context_ref) : null} />;
    case 'question':
      return <QuestionCard item={item} pending={pending} onAct={onAct} onAddRace={onAddRace} readOnly={readOnly} />;
    case 'proposal':
      if (isStrategyProposal(item)) {
        const done = workFor(item, work);
        return (
          <div className="space-y-2">
            {done && <CreationWork view={done} />}
            <StrategyProposalCard item={item} onAct={onAct} onWhy={onWhy} busy={busy} today={today} races={races} readOnly={readOnly}
              reply={itemOutcome(item, outcomes)} />
          </div>
        );
      }
      return <ChangeCard item={item} onAct={onAct} onWhy={onWhy} pending={pending} now={now} readOnly={readOnly}
        reply={itemOutcome(item, outcomes)} />;
    case 'change':
      return <ChangeCard item={item} onAct={onAct} onWhy={onWhy} pending={pending} now={now} readOnly={readOnly} />;
    case 'working':
      return <Working item={item} work={work} />;
    case 'plan_update':
      if (isPhaseReview(item)) return <PhaseReviewCard item={item} onOpenTimeline={onOpenTimeline} onWhy={onWhy} />;
      return <PlanUpdate item={item} onWhy={onWhy} />;
    case 'observation':
      return <Observation item={item} onWhy={onWhy} />;
    case 'safety':
      return <SafetyCard item={item} onTalk={onTalk} readOnly={readOnly} />;
    case 'system_action':
    case 'athlete_action':
      return <SystemLine text={b.text || b.label || b.title} />;
    default:
      return null;
  }
}
